/**
 * @graphql cc
 */
import gql from 'graphql-tag';
import { graphql } from 'react-relay';
import { useCallback, useMemo } from 'react';
import {
	// eslint-disable-next-line graphql-relay-compat/no-import-apollo-functions -- Read http://go/cfe-no-import-apollo-functions
	useMutation,
} from 'react-apollo';

import { markErrorAsHandled } from '@confluence/graphql/entry-points/markErrorAsHandled.ts';
import { useAccessStatus } from '@confluence/session-data/entry-points/useAccessStatus.ts';
import { expVal } from '@confluence/feature-experiments';
import { useQueryInterop } from '@confluence/apollo-relay-interop/entry-points/useQueryInterop.ts';
import { isViewPage, isBlogPage } from '@confluence/named-routes/entry-points/routes-utils.ts';
import { useRouteName } from '@confluence/route-manager/entry-points/RouteState.ts';
import {
	VIEW_PAGE,
	VIEW_BLOG,
	VIEW_BLOG_DATE_LEGACY,
} from '@confluence/named-routes/entry-points/routes.ts';
import { readQueryInterop } from '@confluence/apollo-relay-interop/entry-points/readQuery.ts';

import { isExpectedError } from '../../utils/isExpectedError.ts';
import {
	OnboardingKeysCombined,
	type OnboardingKeysCombinedType,
} from '../../constants/onboarding-keys-combined.ts';
import { filterOnboardingState } from '../../utils/filterOnboardingState.ts';

import onboardingStateGetOnboardingStateQuery, {
	type onboardingStateGetOnboardingStateQuery$data,
	type onboardingStateGetOnboardingStateQuery$variables,
} from './__generated__/onboardingStateGetOnboardingStateQuery.graphql.ts';
import type {
	OnboardingStateInput,
	onboardingStateSetOnboardingStateMutation as onboardingStateSetOnboardingStateMutation$data,
	onboardingStateSetOnboardingStateMutationVariables as onboardingStateSetOnboardingStateMutation$variables,
} from './__types__/onboardingStateSetOnboardingStateMutation.ts';

// Relay generated types are immutable (readonly) whereas Apollo expects mutable types
type OnboardingStateData = {
	onboardingState?: readonly { key: string; value: string | null | undefined }[] | null;
};

// Check if we're on a view page route (VIEW_PAGE or VIEW_BLOG variants)
const isOnViewPageRoute = (): boolean => isViewPage() || isBlogPage();

// Hook to check if we're on a view page route
const useIsViewPageRoute = (): boolean =>
	useRouteName({
		selector: (routeName: string | undefined) =>
			routeName === VIEW_PAGE.name ||
			routeName === VIEW_BLOG.name ||
			routeName === VIEW_BLOG_DATE_LEGACY.name,
	});

// Get the combined onboarding state from cache, returns null if not cached
const getCombinedQueryFromCache = (): onboardingStateGetOnboardingStateQuery$data | null => {
	try {
		return readQueryInterop<
			onboardingStateGetOnboardingStateQuery$data,
			onboardingStateGetOnboardingStateQuery$variables
		>({
			query: onboardingStateGetOnboardingStateQuery,
			variables: { key: OnboardingKeysCombined as unknown as string[] },
		});
	} catch {
		return null;
	}
};

export const deserializeState = (
	data: onboardingStateGetOnboardingStateQuery$data | OnboardingStateData | null | undefined,
) => {
	const state = {} as any;
	// new graphQL call returns onboardingState array
	const { onboardingState } = data || {};

	// parse data and populate state accordingly
	if (onboardingState !== undefined && onboardingState !== null) {
		for (const { key, value } of onboardingState) {
			// key comes back as "{prefix}:{property}" format
			// split by ':' to get parsed {property}
			// some keys don't have a prefix, which is why we have the ||
			const parsedKey = key.split(':')[1] || key;
			if (!value) {
				continue;
			}
			if (/(true|false)/g.test(value)) {
				state[parsedKey] = value === 'true';
			} else {
				state[parsedKey] = value;
			}
		}
	}
	return state;
};

export const useSetOnboardingState = () => {
	const [setState, { error, called }] = useMutation<
		onboardingStateSetOnboardingStateMutation$data,
		onboardingStateSetOnboardingStateMutation$variables
	>(
		gql`
			mutation onboardingStateSetOnboardingStateMutation($states: [OnboardingStateInput!]!) {
				setOnboardingState(states: $states) {
					key
					value
				}
			}
		`,
		{
			update(cache, { data }) {
				const states = data!.setOnboardingState;
				states.forEach(({ key, value }) => {
					// writeFragment can only handle a single data item at a time
					cache.writeFragment({
						id: `OnboardingState:${key}`,
						fragment: gql`
							fragment myKey on OnboardingState {
								key
								value
							}
						`,
						data: {
							key,
							value,
							__typename: 'OnboardingState',
						},
					});
				});
			},
			onError: (error) => {
				if (isExpectedError(error)) {
					markErrorAsHandled(error);
				}
			},
		},
	);

	type SetOnboardingStateArgs = OnboardingStateInput | OnboardingStateInput[];

	const setOnboardingState = useCallback(
		(newOnboardingState: SetOnboardingStateArgs) => {
			let states: OnboardingStateInput[];
			if (Array.isArray(newOnboardingState)) {
				// supports batch updates
				states = newOnboardingState;
			} else {
				// backwards compability for single key-value pair
				states = [newOnboardingState];
			}

			void setState({
				variables: {
					states,
				},
				optimisticResponse: {
					setOnboardingState: states.map(({ key, value }) => ({
						key,
						value,
						__typename: 'OnboardingState',
					})),
				},
			});
		},
		[setState],
	);

	return { setOnboardingState, error, called };
};

// safeguards against empty arrays or strings
const hasValidKeysToFetch = (
	key: OnboardingKeysCombinedType | OnboardingKeysCombinedType[],
): boolean => {
	if (Array.isArray(key)) return key.length > 0;
	if (typeof key === 'string') return key.length > 0;
	return false;
};

/**
 * Reads onboarding state from Apollo cache and returns filtered results.
 * When the centralized experiment is enabled and we're on a view page route,
 * reads with all keys to hit cache, then filters to only the requested keys.
 *
 * @param requestedKeys - The keys the consumer wants to fetch
 * @returns The filtered onboarding state data containing only requested keys
 */
export const getOnboardingStateFromCache = (
	requestedKeys: string[],
): onboardingStateGetOnboardingStateQuery$data | null => {
	const variables =
		isOnViewPageRoute() && expVal('confluence_centralized_onboarding_state', 'isEnabled', false)
			? { key: OnboardingKeysCombined as unknown as string[] }
			: { key: requestedKeys };

	const data = readQueryInterop<
		onboardingStateGetOnboardingStateQuery$data,
		onboardingStateGetOnboardingStateQuery$variables
	>({
		query: onboardingStateGetOnboardingStateQuery,
		variables,
	});

	if (
		!(
			expVal('confluence_centralized_onboarding_state', 'isEnabled', false) && isOnViewPageRoute()
		) ||
		!data
	) {
		return data;
	}

	return filterOnboardingState(data, requestedKeys) as onboardingStateGetOnboardingStateQuery$data;
};

/**
 * TODO: Migrate to fetch from userPreferences - this is considered deprecated
 *
 * WARNING: use `subscribeToCache` sparingly and only where immediate reactivity is strictly required
 * `subscribeToCache`: When true, bypasses the centralized onboarding state (non-reactive cache)
 * and uses `useQueryInterop` with individual keys for reactive updates
 * (e.g., after useSetOnboardingState is called)
 */
export const useGetOnboardingState = (
	key: OnboardingKeysCombinedType | OnboardingKeysCombinedType[],
	skip = false,
	subscribeToCache = false,
) => {
	const { isLicensed, isGuest } = useAccessStatus();
	const isAllowed = isLicensed || isGuest;
	const isViewPageRoute = useIsViewPageRoute();

	const hasKeysToFetch = hasValidKeysToFetch(key);
	const skipQuery = skip || !isAllowed || !hasKeysToFetch;

	// When enabled and on a view page route, use ALL keys to ensure cache hits across different consumers.
	// Only use the combined keys if the cache is already warm (preloaded), otherwise use requested keys.
	// We read from cache once here - if data exists, we skip the query and use it directly.
	const cachedCombinedData =
		!subscribeToCache &&
		isViewPageRoute &&
		expVal('confluence_centralized_onboarding_state', 'isEnabled', false)
			? getCombinedQueryFromCache()
			: null;
	const hasCachedCombinedData = cachedCombinedData !== null;

	const {
		data: queryData,
		error,
		loading,
		refetch,
	} = useQueryInterop<
		onboardingStateGetOnboardingStateQuery$data,
		onboardingStateGetOnboardingStateQuery$variables
	>(
		graphql`
			query onboardingStateGetOnboardingStateQuery($key: [String]) {
				onboardingState(key: $key) {
					key
					value
				}
			}
		`,
		{
			variables: { key: key as unknown as string[] },
			skip: skipQuery || hasCachedCombinedData,
		},
	);

	// Use cached combined data if available, otherwise use query data.
	// Filter the response to only include the keys that the consumer requested.
	const data = useMemo(() => {
		if (hasCachedCombinedData) {
			return filterOnboardingState(cachedCombinedData, key);
		}
		return queryData;
	}, [hasCachedCombinedData, cachedCombinedData, queryData, key]);

	return {
		data,
		error,
		loading,
		refetch,
	};
};
